import { Link } from 'react-router-dom';

import { IThread } from '@chainlit/react-client';

import ThreadOptionsMenu from '@/components/chat/DMThreadOptionsMenu';
import { Badge } from '@/components/ui/badge';

interface ThreadListItemProps {
    thread: IThread;
    currentUserIdentifier?: string;
    showBorder?: boolean;
    onUnpin?: (threadId: string) => void;
    userIsAdmin?: boolean;
}

export default function ThreadListItem({
    thread,
    currentUserIdentifier,
    showBorder = true,
    onUnpin,
    userIsAdmin = false
}: ThreadListItemProps) {
    const isOwner = thread.userIdentifier === currentUserIdentifier;
    const canUnpin = isOwner || userIsAdmin;

    const formatDate = (dateString: string | number) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Link
            to={`/thread/${thread.id}`}
            className={`
                group bg-background flex flex-row gap-3 items-center min-w-0 p-4
                hover:bg-accent/50 active:bg-accent/70 transition-colors
                ${showBorder ? 'border-b border-border' : ''}
            `}
        >
            <div className="flex flex-col flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className="max-w-[calc(100%-10rem)] font-medium text-foreground text-base truncate leading-5">
                        {thread.name || 'Untitled Thread'}
                    </span>
                    {isOwner && (
                        <Badge
                            variant="outline"
                            className="h-5 sm:h-[22px] text-xs"
                        >
                            {'Me'}
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-2 sm:gap-0">
                    <p className="text-muted-foreground text-sm sm:text-sm truncate leading-5">
                        {thread.userIdentifier || 'Unknown author'}
                    </p>
                    <span className="sm:hidden text-muted-foreground text-xs">
                        •
                    </span>
                    <span className="sm:hidden text-muted-foreground text-xs shrink-0">
                        {thread.createdAt ? formatDate(thread.createdAt) : ''}
                    </span>
                </div>
            </div>
            <div className="group/menu relative flex items-center shrink-0">
                <span
                    className={`hidden sm:block text-muted-foreground text-sm ${
                        canUnpin && onUnpin
                            ? 'group-hover:invisible group-has-data-[state=open]/menu:invisible'
                            : ''
                    }`}
                >
                    {thread.createdAt ? formatDate(thread.createdAt) : ''}
                </span>
                {canUnpin && onUnpin && (
                    <ThreadOptionsMenu
                        onUnpin={() => onUnpin(thread.id)}
                        className="sm:invisible visible sm:group-hover:visible group-has-data-[state=open]/menu:visible sm:top-1/2 sm:right-0 sm:absolute sm:-translate-y-1/2"
                    />
                )}
            </div>
        </Link>
    );
}
